/** Instance-owned powerups extracted from the separately maintained Steam Punk game.
 * The host supplies clock, randomness, DOM and lifecycle; no globals or score API are shared. */
export function installSteamPowerups(runtime) {
  runtime.collideOrbs = function (ball) {
    for (let i = runtime.game.orbs.length - 1; i >= 0; i -= 1) {
      const orb = runtime.game.orbs[i];
      const dx = ball.x - orb.x;
      const dy = ball.y - orb.y;
      if (dx * dx + dy * dy > (ball.r + orb.r) ** 2) continue;
      runtime.game.orbs.splice(i, 1);
      runtime.markBoardSceneDirty();
      runtime.collectOrb(orb, ball);
    }
  };
  runtime.collidePlanets = function (ball) {
    for (let i = runtime.game.planets.length - 1; i >= 0; i -= 1) {
      const planet = runtime.game.planets[i];
      const dx = ball.x - planet.x;
      const dy = ball.y - planet.y;
      const dist = Math.hypot(dx, dy);
      if (dist <= 0 || dist > ball.r + planet.r) continue;
      const cooldownKey = String(planet.id);
      if (ball.planetCooldowns[cooldownKey]) continue;
      const nx = dx / dist;
      const ny = dy / dist;
      const dot = ball.vx * nx + ball.vy * ny;
      ball.vx -= 2 * dot * nx;
      ball.vy -= 2 * dot * ny;
      const kick = 1.09;
      ball.vx *= kick;
      ball.vy *= kick;
      runtime.clampBallSpeed(ball, 3200);
      ball.x = planet.x + nx * (planet.r + ball.r + 0.5);
      ball.y = planet.y + ny * (planet.r + ball.r + 0.5);
      planet.hits += ball.neutron ? runtime.NEUTRON_BOMB_POWER : 1;
      runtime.markBoardSceneDirty();
      ball.planetCooldowns[cooldownKey] = 0.09;
      ball.glow = 1;
      runtime.addImpactFlash(
        ball.x,
        ball.y,
        ball.neutron ? '#fff8f0' : '#ffd166',
        ball.neutron ? 3.2 : 1.6,
      );
      runtime.spendTemporaryHit(ball);
      if (ball.dead) return;
      if (planet.hits >= planet.maxHits) {
        runtime.explodePlanet(planet, { triggerBall: ball });
        runtime.game.planets.splice(i, 1);
        runtime.markBoardSceneDirty();
      }
    }
  };
  runtime.getNeutronStarCollapseMultiplier = function (triggerBall) {
    if (!triggerBall?.neutron) return 1;
    runtime.game.neutronStarChain += 1;
    return (
      runtime.NEUTRON_CHAIN_BASE_MULTIPLIER *
      runtime.NEUTRON_CHAIN_STEP_MULTIPLIER ** (runtime.game.neutronStarChain - 1)
    );
  };
  runtime.randomNeutronPlatitude = function () {
    return (
      runtime.NEUTRON_PLATITUDES[
        Math.floor(runtime.random() * runtime.NEUTRON_PLATITUDES.length)
      ] || 'Amazing Skills'
    );
  };
  runtime.awardSpeedNeutronTimerBonus = function (x, y) {
    if (
      runtime.game.mode !== 'speed' ||
      !runtime.game.modeRunStarted ||
      runtime.game.status !== 'playing'
    )
      return;
    runtime.game.modeTimerRemaining += runtime.NEUTRON_SPEED_TIMER_BONUS_SECONDS;
    runtime.addFloater(x, y, `+${runtime.NEUTRON_SPEED_TIMER_BONUS_SECONDS}s`, '#9de7d3', {
      scale: 1.55,
      life: 1.55,
      stroke: 'rgba(5, 38, 46, 0.92)',
      shadowBlur: 30,
    });
    runtime.game.mods.unshift(`Neutron time bonus: +${runtime.NEUTRON_SPEED_TIMER_BONUS_SECONDS}s`);
    runtime.game.mods.length = 4;
  };
  runtime.explodePlanet = function (planet, options = {}) {
    const multiplier = runtime.getNeutronStarCollapseMultiplier(options.triggerBall);
    const supercharged = multiplier > 1;
    const radiusScale = supercharged ? runtime.clamp(Math.sqrt(multiplier), 1, 18) : 1;
    const fxScale = supercharged ? runtime.clamp(1 + Math.log10(multiplier), 1, 5) : 1;
    const blastRadius = runtime.ROW_STEP * 2.8 * radiusScale;
    const burstBallPower = runtime.getCurrentPlayerBallDamage();
    for (const brick of runtime.game.bricks) {
      if (brick.dead) continue;
      const bx = brick.x + brick.w / 2;
      const by = brick.y + brick.h / 2;
      const distance = Math.hypot(bx - planet.x, by - planet.y);
      if (distance > blastRadius) continue;
      const damage = Math.max(24, Math.round(90 * multiplier * (1 - distance / blastRadius)));
      if (damage <= 0) continue;
      brick.hp -= damage;
      brick.heat = 1;
      runtime.markBoardSceneDirty();
      if (brick.hp <= 0) {
        runtime.destroyBrick(brick);
        runtime.addScore(brick.maxHp * runtime.NEUTRON_COLLAPSE_BLOCK_SCORE_MULTIPLIER, bx, by);
        runtime.addCoins(1 + Math.floor(brick.maxHp / 16), bx, by + 12);
      }
    }
    runtime.game.spatialDirty = true;
    runtime.checkBoardClearReward(planet.x, planet.y);
    runtime.awardNeutronFuel(planet);
    const burstCount = Math.min(240, Math.floor(runtime.rand(20, 51) * fxScale));
    for (let i = 0; i < burstCount; i += 1) {
      const angle = (360 / burstCount) * i + runtime.rand(-8, 8);
      runtime.spawnBurstBall(
        planet.x + runtime.rand(-4, 4),
        planet.y + runtime.rand(-4, 4),
        angle,
        runtime.rand(560, 1040) * runtime.clamp(fxScale, 1, 3),
        { power: burstBallPower },
      );
    }
    runtime.spawnNeutronRewardPowerups(planet);
    runtime.addScore(
      planet.mass,
      planet.x,
      planet.y,
      supercharged ? `STAR POWER x${runtime.formatBigNumber(multiplier)}` : `STAR +${planet.mass}`,
    );
    runtime.addCoins(
      Math.max(4, Math.floor((planet.mass / 300) * fxScale)),
      planet.x,
      planet.y + 22,
    );
    if (supercharged) {
      runtime.addExactCoins(
        Math.floor(fxScale * 25),
        planet.x,
        planet.y + 48,
        runtime.randomNeutronPlatitude(),
      );
      runtime.awardSpeedNeutronTimerBonus(planet.x, planet.y + 76);
    }
    runtime.explode(
      planet.x,
      planet.y,
      supercharged ? '#fff8f0' : '#ff8a3d',
      Math.min(420, Math.floor(120 * fxScale)),
      1.6 * fxScale,
    );
    runtime.shockwave(planet.x, planet.y, supercharged ? '#fff8f0' : '#ffd166', fxScale);
    runtime.addBurst(planet.x, planet.y, '#fff4c2', 24 * fxScale, 320 * fxScale, 0.78, 6 * fxScale);
    runtime.addBurst(
      planet.x,
      planet.y,
      supercharged ? '#ffffff' : '#ff8a3d',
      42 * fxScale,
      250 * fxScale,
      0.86,
      4 * fxScale,
    );
    for (let i = 0; i < (supercharged ? 9 : 5); i += 1) {
      runtime.scheduleEffect(i * 0.055, () =>
        runtime.spark(planet.x, planet.y, i % 2 ? '#fff8f0' : '#ff8a3d', supercharged ? 54 : 32),
      );
    }
    runtime.game.mods.unshift(
      supercharged
        ? `Neutron star superburst x${runtime.formatBigNumber(multiplier)}`
        : `Neutron star burst: +${burstCount} balls`,
    );
    runtime.game.mods.length = 4;
    runtime.game.screenFlash = supercharged ? Math.max(runtime.game.screenFlash, 1.6) : 1;
    runtime.game.shake = supercharged
      ? Math.max(runtime.game.shake, Math.min(4, 1.4 + fxScale * 0.55))
      : 1;
  };
  runtime.collectOrb = function (orb, ball) {
    runtime.addImpactFlash(orb.x, orb.y, runtime.orbColor(orb.type), 1.35);
    if (orb.type === 'ball') {
      runtime.game.ballCount += 1;
      runtime.addFloater(orb.x, orb.y, '+BALL', '#74ff8d');
      runtime.game.mods.unshift('Extra ball collected');
    } else if (orb.type === 'split') {
      runtime.spawnBall(runtime.game.angle + (runtime.random() < 0.5 ? -12 : 12), 0, {
        source: 'split',
      });
      runtime.addFloater(orb.x, orb.y, 'SPLIT', '#ffd166');
      runtime.game.mods.unshift('Split shot triggered');
    } else if (orb.type === 'coin') {
      runtime.addCoins(7, orb.x, orb.y);
      runtime.game.mods.unshift('Cash surge collected');
    } else if (orb.type === 'blast') {
      runtime.blast(ball.x, ball.y);
      runtime.game.mods.unshift('Blast orb detonated');
    } else {
      runtime.triggerSpecialPowerup(orb.type, orb.x, orb.y);
    }
    runtime.game.mods.length = 4;
    runtime.syncHud();
  };
  runtime.triggerSpecialPowerup = function (type, x, y) {
    if (type === 'clear5') {
      runtime.clearLowestRows(5, x, y);
      runtime.game.mods.unshift('Powerup: lowest 5 rows cleared');
      runtime.addFloater(x, y, 'CLEAR 5', '#ffd166');
    } else if (type === 'clear10') {
      runtime.clearLowestRows(10, x, y);
      runtime.game.mods.unshift('Powerup: lowest 10 rows cleared');
      runtime.addFloater(x, y, 'CLEAR 10', '#ffd166');
    } else if (type === 'halve') {
      runtime.halveAllBricks(x, y);
      runtime.game.mods.unshift('Powerup: all bricks cut in half');
      runtime.addFloater(x, y, 'HALF WIPE', '#ff8a3d');
    } else if (type === 'chaos') {
      runtime.game.chaosTurns += 3;
      runtime.game.chaosStacks += 1;
      runtime.game.mods.unshift(
        `Chaos mode: ${runtime.chaosMultiplierLabel()} rewards, ${runtime.game.chaosTurns} turns`,
      );
      runtime.addFloater(x, y, `CHAOS ${runtime.chaosMultiplierLabel()}`, '#ffd166');
    }
    runtime.game.screenFlash = 0.75;
    runtime.explode(x, y, runtime.orbColor(type), 78, 1.4);
    runtime.addBurst(x, y, runtime.orbColor(type), 18, 220, 0.55, 4);
    runtime.game.shake = Math.max(runtime.game.shake, 0.45);
  };
  runtime.clearLowestRows = function (rowCount, x, y) {
    const rows = [];
    for (const brick of runtime.game.bricks) {
      if (brick.dead) continue;
      const row = Math.round(brick.y / runtime.ROW_STEP);
      if (!rows.includes(row)) rows.push(row);
    }
    rows.sort((a, b) => b - a);
    const targetRows = rows.slice(0, rowCount);
    for (const brick of runtime.game.bricks) {
      if (brick.dead || !targetRows.includes(Math.round(brick.y / runtime.ROW_STEP))) continue;
      runtime.destroyBrick(brick);
      runtime.addScore(brick.maxHp * 10, brick.x + brick.w / 2, brick.y + brick.h / 2);
    }
    runtime.game.spatialDirty = true;
    runtime.checkBoardClearReward(x, y);
    runtime.shockwave(x, y, '#ffd166');
    runtime.addBurst(x, y, '#ffd166', 30, 260, 0.62, 4);
  };
  runtime.halveAllBricks = function (x, y) {
    for (const brick of runtime.game.bricks) {
      if (brick.dead) continue;
      const loss = Math.max(1, Math.ceil(brick.hp * 0.5));
      brick.hp -= loss;
      brick.heat = 1;
      runtime.markBoardSceneDirty();
      runtime.addScore(loss * 5, brick.x + brick.w / 2, brick.y + brick.h / 2, `-${loss}`);
      if (brick.hp <= 0) runtime.destroyBrick(brick);
    }
    runtime.game.spatialDirty = true;
    runtime.checkBoardClearReward(x, y);
    runtime.shockwave(x, y, '#ff8a3d');
    runtime.addBurst(x, y, '#ff8a3d', 30, 250, 0.62, 4);
  };
  runtime.collideWalls = function (ball) {
    for (let i = runtime.game.walls.length - 1; i >= 0; i -= 1) {
      const wall = runtime.game.walls[i];
      const lx = (ball.x - wall.cx) * wall.negCos - (ball.y - wall.cy) * wall.negSin;
      const ly = (ball.x - wall.cx) * wall.negSin + (ball.y - wall.cy) * wall.negCos;
      if (Math.abs(lx) > wall.hx + ball.r || Math.abs(ly) > wall.hy + ball.r) continue;
      const nx =
        Math.abs(wall.hx - Math.abs(lx)) < Math.abs(wall.hy - Math.abs(ly)) ? Math.sign(lx) : 0;
      const ny = nx === 0 ? Math.sign(ly) : 0;
      const normalX = nx * wall.cos - ny * wall.sin;
      const normalY = nx * wall.sin + ny * wall.cos;
      const dot = ball.vx * normalX + ball.vy * normalY;
      ball.vx -= 2 * dot * normalX;
      ball.vy -= 2 * dot * normalY;
      ball.wallBounceStreak += 1;
      if (Math.abs(normalX) < 0.35) ball.sideBounceStreak = 0;
      runtime.preventBounceStall(ball, Math.abs(normalX) > 0.65 ? 'side' : 'top');
      wall.hp -= 1;
      wall.heat = 1;
      runtime.markBoardSceneDirty();
      ball.glow = Math.max(ball.glow, 0.45);
      runtime.addImpactFlash(ball.x, ball.y, '#ffd166', 0.8);
      runtime.spendTemporaryHit(ball);
      if (ball.dead) return;
      if (wall.hp <= 0) {
        runtime.game.walls.splice(i, 1);
        runtime.addScore(18, wall.cx, wall.cy, '+18 rail');
        runtime.addCoins(1, wall.cx, wall.cy + 12, '+$1 rail');
        runtime.explode(wall.cx, wall.cy, wall.tunnel ? '#ffd166' : '#b86b2d', 44, 1.15);
      }
    }
  };
  runtime.blast = function (x, y) {
    for (const brick of runtime.game.bricks) {
      if (brick.dead) continue;
      const bx = brick.x + brick.w / 2;
      const by = brick.y + brick.h / 2;
      const dist = Math.hypot(bx - x, by - y);
      if (dist > 145) continue;
      brick.hp -= Math.ceil(2 + runtime.game.level * 0.12);
      brick.heat = 1;
      runtime.markBoardSceneDirty();
      if (brick.hp <= 0) {
        runtime.destroyBrick(brick);
        runtime.addScore(brick.maxHp * 10, bx, by);
        runtime.addCoins(1 + Math.floor(brick.maxHp / 18), bx, by + 12);
        runtime.explode(bx, by, '#ff8a3d', 32, 1);
      }
    }
    runtime.checkBoardClearReward(x, y);
    runtime.addBurst(x, y, '#ff8a3d', 22, 230, 0.52, 4);
    runtime.game.shake = Math.max(runtime.game.shake, 0.35);
  };
  runtime.endVolley = function () {
    runtime.game.volleyActive = false;
    runtime.game.status = 'ready';
    if (!runtime.game.roundAdvancedForVolley) runtime.advanceRoundAfterVolley();
    runtime.game.volleyStartedAt = 0;
    runtime.game.roundAdvancedForVolley = false;
    runtime.syncHud();
  };
  runtime.advanceRoundAfterVolley = function (options = {}) {
    if (runtime.game.roundAdvancedForVolley && !options.force) return;
    runtime.game.roundAdvancedForVolley = true;
    if (runtime.game.chaosTurns > 0) {
      runtime.game.chaosTurns -= 1;
      if (runtime.game.chaosTurns <= 0) runtime.game.chaosStacks = 0;
      runtime.game.mods.unshift(
        runtime.game.chaosTurns > 0
          ? `Chaos ${runtime.chaosMultiplierLabel()}: ${runtime.game.chaosTurns} turns left`
          : 'Chaos mode ended',
      );
      runtime.game.mods.length = 4;
    }
    return runtime.spawnWave(true);
  };
  runtime.buyUpgrade = function (type, options = {}) {
    const cost = runtime.game.costs[type];
    if (
      !Number.isFinite(cost) ||
      runtime.game.coins < cost ||
      runtime.game.status === 'gameover' ||
      runtime.isUpgradeCapped(type)
    )
      return false;
    runtime.game.coins -= cost;
    runtime.game.costs[type] = Math.ceil(cost * 1.82 + 18);
    if (type === 'balls') {
      runtime.game.ballCount += 1;
      runtime.game.mods.unshift('Permanent ball added');
    }
    if (type === 'impact') {
      runtime.game.crit += 1;
      runtime.game.mods.unshift('Bonus payout upgraded');
    }
    if (type === 'launchTime') {
      runtime.game.launchWaitSeconds = Math.max(
        runtime.MIN_LAUNCH_WAIT_SECONDS,
        runtime.game.launchWaitSeconds - runtime.LAUNCH_WAIT_STEP_SECONDS,
      );
      runtime.game.mods.unshift(
        `Launch wait ${runtime.formatLaunchWaitSeconds(runtime.game.launchWaitSeconds)}`,
      );
    }
    if (type === 'powerupChance') {
      runtime.game.powerupChanceBonus = Math.min(
        runtime.POWERUP_CHANCE_MAX_BONUS,
        runtime.game.powerupChanceBonus + runtime.POWERUP_CHANCE_STEP,
      );
      runtime.game.mods.unshift(
        `Powerup chance +${Math.round(runtime.game.powerupChanceBonus * 100)}%`,
      );
    }
    runtime.game.mods.length = 4;
    if (!options.skipSync) runtime.syncHud();
    return true;
  };
  runtime.isUpgradeCapped = function (type) {
    if (type === 'launchTime')
      return runtime.game.launchWaitSeconds <= runtime.MIN_LAUNCH_WAIT_SECONDS;
    if (type === 'powerupChance')
      return runtime.game.powerupChanceBonus >= runtime.POWERUP_CHANCE_MAX_BONUS;
    return false;
  };
  runtime.runAutoUpgrade = function () {
    if (
      !runtime.game.autoUpgrade ||
      runtime.game.autoUpgradeRunning ||
      runtime.game.status === 'gameover'
    )
      return;
    runtime.game.autoUpgradeRunning = true;
    let purchases = 0;
    try {
      while (purchases < 64) {
        const next = Object.entries(runtime.game.costs)
          .filter(
            ([type, cost]) =>
              Number.isFinite(cost) && !runtime.isUpgradeCapped(type) && runtime.game.coins >= cost,
          )
          .sort((a, b) => a[1] - b[1])[0];
        if (!next) break;
        if (!runtime.buyUpgrade(next[0], { skipSync: true })) break;
        purchases += 1;
      }
    } finally {
      runtime.game.autoUpgradeRunning = false;
    }
    if (purchases > 0) runtime.syncHud();
  };
}
